import { cn } from '@crowdex/ui/lib/utils';
import { FileBox, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { AllAppsTile, ToolTile } from '@/components/desktop-side-panel/new-tab-view';
import { usePanelItems } from '@/components/desktop-side-panel/use-panel-items';
import type { PanelInstance } from '@/lib/side-panel/panel-instance';
import type { PanelToolEntry } from '@/lib/side-panel/panel-tools';

function SheetSection({ children, title }: { children: ReactNode; title: string }) {
  return (
    <section aria-label={title} className="flex flex-col gap-2">
      <h3 className="type-meta px-1 text-muted-foreground">{title}</h3>
      {children}
    </section>
  );
}

/**
 * The phone's panel sheet (7): the open tabs as pills ("Open · 3", the shown one filled), then
 * the tools and the apps of the conversation as tiles, as the desktop's New tab offers them.
 * Picking one shows it and closes the sheet (`data-panel-tab-choice`).
 */
export function PanelTabList({
  panels,
  activeId,
  tools = [],
  reviewCount,
  onSelectTab,
  onCloseTab,
  onLaunch,
  onAllApps,
}: {
  panels: readonly PanelInstance[];
  activeId: string | null;
  tools?: readonly PanelToolEntry[];
  reviewCount?: number | undefined;
  onSelectTab: (panel: PanelInstance) => void;
  onCloseTab: (panel: PanelInstance) => void;
  onLaunch?: (entry: PanelToolEntry) => void;
  onAllApps?: () => void;
}) {
  const items = usePanelItems();
  const open = panels.filter((panel) => panel.type !== 'new-tab');
  const coreTools = tools.filter((entry) => entry.group === 'tool');
  const apps = tools.filter((entry) => entry.group === 'app');
  return (
    <div className="flex flex-col gap-5 px-4 pb-2">
      {open.length > 0 && (
        <SheetSection title={`Open · ${open.length}`}>
          <ul className="flex flex-wrap gap-2">
            {open.map((panel) => {
              const Icon = panel.type.startsWith('artifact:')
                ? FileBox
                : (items.find((item) => item.id === panel.type)?.icon ?? FileBox);
              const active = panel.id === activeId;
              return (
                <li
                  className={cn(
                    'flex h-10 items-center rounded-lg border',
                    active
                      ? 'border-input bg-control text-foreground'
                      : 'border-hairline text-foreground/85',
                  )}
                  key={panel.id}
                >
                  <button
                    aria-current={active || undefined}
                    className="type-ui flex h-full min-w-0 items-center gap-2 pl-4 font-semibold"
                    data-panel-tab-choice
                    onClick={() => onSelectTab(panel)}
                    type="button"
                  >
                    <Icon className="size-4 shrink-0" />
                    <span className="max-w-40 truncate">{panel.title}</span>
                    {panel.type === 'review' && reviewCount !== undefined && reviewCount > 0 && (
                      <span className="font-normal opacity-70 tabular-nums">{reviewCount}</span>
                    )}
                  </button>
                  <button
                    aria-label={`Close ${panel.title}`}
                    className="flex size-10 shrink-0 items-center justify-center rounded-control opacity-60 active:opacity-100"
                    onClick={() => onCloseTab(panel)}
                    type="button"
                  >
                    <X className="size-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        </SheetSection>
      )}
      {onLaunch !== undefined && coreTools.length > 0 && (
        <SheetSection title="Tools">
          {/* Three a row on the narrowest phones: a tile keeps a name like "Terminal" whole. */}
          <div className="grid grid-cols-3 gap-2 min-[360px]:grid-cols-4">
            {coreTools.map((entry) => (
              <div className="contents" data-panel-tab-choice key={entry.key}>
                <ToolTile compact entry={entry} onLaunch={() => onLaunch(entry)} />
              </div>
            ))}
          </div>
        </SheetSection>
      )}
      {onLaunch !== undefined && (apps.length > 0 || onAllApps !== undefined) && (
        <SheetSection title="Apps on this conversation">
          <div className="grid grid-cols-3 gap-2 min-[360px]:grid-cols-4">
            {apps.map((entry) => (
              <div className="contents" data-panel-tab-choice key={entry.key}>
                <ToolTile compact entry={entry} onLaunch={() => onLaunch(entry)} />
              </div>
            ))}
            {onAllApps !== undefined && (
              <div className="contents" data-panel-tab-choice>
                <AllAppsTile compact onOpen={onAllApps} />
              </div>
            )}
          </div>
        </SheetSection>
      )}
    </div>
  );
}
